import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { doc, getDoc } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import QnACreateQuestion from './QnACreateQuestion';
import QnAList from './QnAList';
import QnADetail from './QnADetail';
import { db } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import QnAQuestion from '../../models/QnAQuestion';
import { COLLECTIONS } from '../../platform/constants';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import { matchesActiveTenant } from '../../services/tenantContentFilter';
import ModulePageHeader from '../common/ModulePageHeader';
import useBrowserBackStack from '../../hooks/useBrowserBackStack';
import { isDetailHeroEnabled, PREMIUM_MODULE_IDS } from '../../platform/contentListVisual';
import './QnA.css';

function QnA() {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showCreateQuestion, setShowCreateQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();
  const isAdmin = isTenantAdminForTenant(memberships, tenantId, user);

  useEffect(() => {
    const questionId = searchParams.get('question');
    if (!questionId || !tenantId) return undefined;

    let cancelled = false;

    getDoc(doc(db, COLLECTIONS.QUESTIONS, questionId))
      .then((snapshot) => {
        if (cancelled || !snapshot.exists()) return;

        const question = QnAQuestion.fromFirestore(snapshot);
        if (!matchesActiveTenant(question, tenantId)) return;

        setSelectedQuestion(question);
        setShowCreateQuestion(false);
        setEditingQuestion(null);
      })
      .finally(() => {
        if (cancelled) return;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('question');
        setSearchParams(nextParams, { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, tenantId]);

  const handleSelectQuestion = (question) => {
    setSelectedQuestion(question);
    setShowCreateQuestion(false);
    setEditingQuestion(null);
  };

  const resetToList = useCallback(() => {
    setSelectedQuestion(null);
    setShowCreateQuestion(false);
    setEditingQuestion(null);
  }, []);

  const onList = !selectedQuestion && !editingQuestion && !showCreateQuestion;
  const closeDetailView = useBrowserBackStack(!onList, resetToList);

  const toggleCreateQuestionForm = () => {
    if (showCreateQuestion) {
      closeDetailView();
      return;
    }
    setShowCreateQuestion(true);
    setSelectedQuestion(null);
    setEditingQuestion(null);
  };

  const handleQuestionCreated = () => {
    setShowCreateQuestion(false);
    setSelectedQuestion(null);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setShowCreateQuestion(false);
    setSelectedQuestion(null);
  };

  const handleQuestionUpdated = () => {
    setEditingQuestion(null);
    setSelectedQuestion(null);
  };

  const useDetailHero = isDetailHeroEnabled(PREMIUM_MODULE_IDS.QNA);
  const headerTitle = selectedQuestion && useDetailHero
    ? undefined
    : selectedQuestion?.title || editingQuestion?.title
      || (showCreateQuestion ? 'Novo vprašanje' : 'Vprašanja');

  return (
    <div className="platform-module-container qna-container">
        <ModulePageHeader
          eyebrow={!onList ? 'Vprašanja' : undefined}
          title={headerTitle}
          subtitle={
            onList ? 'Postavite vprašanje sindikatu ali spremljajte odgovore.' : undefined
          }
          actions={(
            <>
              {!selectedQuestion && !editingQuestion && (
                <Button variant="primary" onClick={toggleCreateQuestionForm}>
                  {showCreateQuestion ? 'Nazaj na vprašanja' : 'Novo vprašanje'}
                </Button>
              )}
              {(selectedQuestion || editingQuestion) && (
                <Button variant="primary" onClick={closeDetailView}>Nazaj na vprašanja</Button>
              )}
            </>
          )}
        />
        {!selectedQuestion && !editingQuestion ? (
          <>
            {showCreateQuestion ? (
              <QnACreateQuestion onQuestionCreated={handleQuestionCreated} />
            ) : (
              <QnAList
                onSelectQuestion={handleSelectQuestion}
                isAdmin={isAdmin}
                onEditQuestion={handleEditQuestion}
              />
            )}
          </>
        ) : editingQuestion ? (
          <QnACreateQuestion
            onQuestionCreated={handleQuestionCreated}
            question={editingQuestion}
            onQuestionUpdated={handleQuestionUpdated}
          />
        ) : (
          <QnADetail
            questionId={selectedQuestion.id}
            selectedQuestion={selectedQuestion}
          />
        )}
    </div>
  );
}

export default QnA;
